<template>
  <div class="info-module" data-cy="info-module">
    <ProductDetailsModal
      v-if="productDetailsModalVisible"
      :job-id="detailsJobId"
      @closeModal="productDetailsModalVisible = false"
    />
    <IngredientDetailsModal
      v-if="ingredientDetailsModalVisible"
      :job-id="detailsJobId"
      @closeModal="ingredientDetailsModalVisible = false"
    />
    <UITabContainer :tabs="tabs" @tab-changed="tabChanged">
      <template v-slot:PlantInformation>
        <PlantsGrid :facilities="facilities" :loading="!tabs[0].loaded" />
      </template>
      <template v-slot:RecentSubmissions>
        <RecentSubmissionsGrid
          :submissions="submissions"
          :loading="!tabs[1].loaded"
          @detailsClicked="onDetailsClicked"
        />
        <a
          href="SubmissionsOverview"
          class="see-more-link"
          data-cy="see-more-link"
          >See More <font-awesome-icon icon="fa-solid fa-angle-right" />
        </a>
      </template>
      <template v-slot:Brands>
        <BrandsGrid :brands="brands" :loading="!tabs[2].loaded" />
      </template>
    </UITabContainer>
  </div>
</template>

<script setup lang="ts">
import UITabContainer from "@/shared/ui/tabs/tab-container.vue";
import { onBeforeMount, ref } from "vue";
import { useMainStore } from "@/stores/useMainStore";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import { useNotificationStore } from "@/stores/useNotificationStore";
import type { GetSubmissionsWithFilteringRequest } from "@/api/submissionsOverview";
import moment from "moment";
import type { SubmissionsTable } from "@/Domain/SubmissionsTable";
import RecentSubmissionsGrid from "./RecentSubmissionsGrid.vue";
import ProductDetailsModal from "../submissionsOverview/ProductDetailsModal.vue";
import IngredientDetailsModal from "../submissionsOverview/IngredientDetailsModal.vue";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { CacheService } from "@/shared/services/CacheService";
import type { Facility } from "@/Domain/Facility";
import PlantsGrid from "./PlantsGrid.vue";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type { BrandNameAndCompanyIdAndName } from "@/Domain/BrandNameAndCompanyIdAndName";
import BrandsGrid from "./BrandsGrid.vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

const submissions = ref<SubmissionsTable[]>([]);
const facilities = ref<Facility[]>([]);
const brands = ref<BrandNameAndCompanyIdAndName[]>([]);

const productDetailsModalVisible = ref<boolean>(false);
const ingredientDetailsModalVisible = ref<boolean>(false);
const detailsJobId = ref<string>("");


const getSubmissionsWithFilteringAsync = async function () {
  const beginDate = moment(new Date()).add(-1, "months");

  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    beginDate: beginDate.toDate().toLocaleDateString("en-US"),
    endDate: new Date().toLocaleDateString("en-US"),
    filters: "",
    take: 5,
    skip: 0,
    sortField: "DateProcessedForSort",
    ascending: false,
    valuesForFieldOnly: "",
  } as GetSubmissionsWithFilteringRequest;

  return handleResponsePromiseAsync(
    api.get("GetSubmissionsWithFiltering", requestParams),
    (data) => {
      submissions.value = data.ListItems;
    },
    "InfoModule.vue",
    "getSubmissionsWithFilteringAsync"
  );
};

const getFacilitiesAsync = async function () {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    includeMainOffice: false,
    subApplication: 10,
    contactId: -1,
    includeSpecial: false,
  };

  await handleResponsePromiseAsync(
    CacheService.getResponseAsync(
      createCacheKey(requestParams, "GetFacilities"),
      () => api.get("GetFacilities", requestParams)
    ),
    (data) => {
      facilities.value = data;
    },
    "InfoModule.vue",
    "getFacilitiesAsync",
    () => {
      notificationStore.notify({
        message: `Plant Info Error Encountered`,
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );
};

const getBrandsAsync = async function () {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    plantList: "",
    subApplication: 3,
  };

  await handleResponsePromiseAsync(
    CacheService.getResponseAsync(
      createCacheKey(requestParams, "GetBrands"),
      () => api.get("VueGetBrands", requestParams)
    ),
    (data) => {
      brands.value = data;
    },
    "InfoModule.vue",
    "getBrandsAsync",
    () => {
      notificationStore.notify({
        message: `Brands Error Encountered`,
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );
};

const tabs = ref([
  {
    id: "PlantInformation",
    label: "Plant Information",
    loaded: false,
    getData: getFacilitiesAsync,
  },
  {
    id: "RecentSubmissions",
    label: "Recent Submissions",
    loaded: false,
    getData: getSubmissionsWithFilteringAsync,
  },
  {
    id: "Brands",
    label: "Brands",
    loaded: false,
    getData: getBrandsAsync,
  },
]);

const onDetailsClicked = function (submission: SubmissionsTable) {
  detailsJobId.value = submission.JobId;
  if (submission.JobType.includes("Products")) {
    productDetailsModalVisible.value = true;
  }
  if (submission.JobType.includes("Ingredients")) {
    ingredientDetailsModalVisible.value = true;
  }
};

const tabChanged = async function (tabId: string) {
  const tab = tabs.value.find((t) => t.id === tabId);
  if (tab && !tab.loaded) {
    await tab.getData();
    tab.loaded = true;
  }
};

onBeforeMount(async () => {
   // Load the first tab data on mount
  tabChanged(tabs.value[0].id);
});
</script>

<style scoped lang="scss">
.info-module {
  border: 1px solid #ddd;
  background: white;

  &__container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin: 12px;
  }

  &__flex {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 16px;

    &:first-child {
      border-bottom: 3px solid #ddd;
      margin-bottom: 10px;
    }
  }

  &__download-button {
    margin-bottom: 10px;
    flex-grow: 1;
    flex-basis: 50%;
  }

  &__download-ukd {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    position: relative;
    flex-grow: 1;
    flex-basis: 50%;

    &-input {
      width: 100%;
    }

    &-icon {
      position: absolute;
      top: 10px;
      right: 12px;
      width: 14px;
    }
  }

  .see-more-link {
    display: flex;
    margin: 10px;
    text-decoration: none;
    gap: 5px;
    align-items: center;
    justify-content: flex-end;
  }
}
</style>

<style lang="scss">
.info-module {
  .tab-header {
    background: #efefef;
    overflow-x: auto;
  }
}
</style>
